import React from 'react';
import './FieldControls.css';
import SanctionsPanel from './SanctionsPanel';
import { useTheme } from '../../../hooks/useTheme';

/**
 * Component for displaying the timer, period controls, score, sanctions and action buttons
 */
const FieldControls = ({
  formattedTime,
  isRunning,
  startTimer,
  pauseTimer,
  resetTimer,
  setTime,
  homeTeamName,
  awayTeamName,
  homeTeamScore,
  awayTeamScore,
  matchPeriod,
  handlePeriodChange,
  handleResetAll,
  exportToCSV,
  saveMatch,
  events,
  showToast,
  // Sanctions-related props
  activeSanctions,
  getRemainingTime,
  getFormattedRemainingTime,
  isInWarningState
}) => {
  // Access theme for conditional styling
  const { theme } = useTheme();
  
  // Handle reset timer with confirmation
  const handleResetTimer = () => {
    if (window.confirm('Are you sure you want to reset the timer? This will set the time back to 00:00 and reset to First Half.')) {
      resetTimer();
      
      // Only change period if we're not already in First Half
      if (matchPeriod !== 'First Half') {
        // Pass a special flag to indicate this is from the reset button
        // This will be used in TrackingPage to bypass the confirmation
        handlePeriodChange('First Half', true);
      }
    }
  };

  return (
    <div className={`field-controls-bar ${theme === 'high-contrast' ? 'high-contrast' : ''}`}>
      {/* Main controls row with timer, score, buttons */}
      <div className="main-controls-row">
        {/* Timer with Period Controls */}
        <div className="field-timer">
          <div className="timer-display">
            <div className="timer-time">{formattedTime}</div>
          </div>
          
          <div className="timer-controls">
            <div className="timer-buttons">
              <button
                className="timer-button start"
                onClick={startTimer}
                disabled={isRunning}
              >
                 <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3"></polygon>
  </svg>
              </button>
              <button
                className="timer-button pause"
                onClick={pauseTimer}
                disabled={!isRunning}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="4" width="4" height="16"></rect>
    <rect x="14" y="4" width="4" height="16"></rect>
  </svg>
              </button>
              <button
                className="timer-button reset"
                onClick={handleResetTimer}
              >
                Reset
              </button>              
            </div>
            
            <div className="period-buttons">
              <button
                className={`period-button ${matchPeriod === 'First Half' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('First Half')}
              >
                1st Half
              </button>
              <button
                className={`period-button ${matchPeriod === 'Second Half' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('Second Half')}
              >
                2nd Half
              </button>
              <button
                className={`period-button ${matchPeriod === 'Overtime' ? 'active' : ''}`}
                onClick={() => handlePeriodChange('Overtime')}
              >
                Overtime
              </button>
            </div>
          </div>
        </div>

        {/* Score */}
        <div className="field-score-container">
          <div className="field-score">
            <div className="team-score">
              <span className="score-label home-team">{homeTeamName}</span>
              <span className="score-number">{homeTeamScore}</span>
            </div>
            <div className="team-score">
              <span className="score-label away-team">{awayTeamName}</span>
              <span className="score-number">{awayTeamScore}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="field-buttons">
          <button className="reset-data-button" onClick={handleResetAll}>Delete Data</button>
          {/* Save Match Button */}
          <button 
            className="save-match-button" 
            onClick={saveMatch}
            disabled={events.length === 0}
            title={events.length === 0 ? "No events to save" : "Save match to database"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
          </button>
          {/* Export to CSV button */}
          <button 
            className="export-btn" 
            onClick={exportToCSV}
            disabled={events.length === 0}
            title={events.length === 0 ? "No events to export" : "Export to CSV"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </button>
        </div>
      </div>
      
      {/* Sanctions Panel (full width row below) */}
      {(() => {
        if (activeSanctions && activeSanctions.length > 0) {
          return (
            <div className="sanctions-row">
              <SanctionsPanel
                activeSanctions={activeSanctions}
                homeTeamName={homeTeamName}
                awayTeamName={awayTeamName}
                getRemainingTime={getRemainingTime}
                getFormattedRemainingTime={getFormattedRemainingTime}
                isInWarningState={isInWarningState}
              />
            </div>
          );
        } else {
          return null;
        }
      })()}
    </div>
  );
};

export default FieldControls;